import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Flame, ScanLine, Trophy, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import * as sfx from "../lib/sfx";
import LeaderboardModal from "./LeaderboardModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const W = 1568;
const H = 880;
const LAUNCH = { x: 470, y: 398 };
const GRILL = { x1: 1240, x2: 1510, y: 620 };
const GROUND = 762;
const GRAV = 1500;
const ANGLE = (62 * Math.PI) / 180;
const SWEET = [66, 74];
const CHARGE_SPEED = 128;
const NIGHT_COMBO = 5;
const MOVE_COMBO = 10;
const NEON_COMBO = 15;
const WIN_COMBO = 50;
const NEON_PARAM = new URLSearchParams(window.location.search).get("neon") === "1";
const TIER_PARAM = parseInt(new URLSearchParams(window.location.search).get("tier") || "0", 10) || 0;
const FLAME_COLORS = ["#FF3B30", "#FF9500", "#FFD60A", "#FF6B00"];
const GOOD_MSGS = ["NICE FLIP!", "SIZZLER!", "SERVED HOT!", "ON THE GRATE!"];
const MISS_MSGS = ["ICED!", "OFF THE POST!", "IN THE GRASS!", "SIN BIN!"];
const STARS = Array.from({ length: 46 }, (_, i) => [
    (i * 347 + 123) % W,
    ((i * 191 + 57) % 360) + 20,
]);

const hash = (i, j) => (i * 31 + j * 17 + 101) % 9;

function drawSpatula(ctx, px, py, angle) {
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(angle);
    ctx.scale(1.25, 1.25);
    // handle with tape stripes
    ctx.fillStyle = "#17171D";
    ctx.fillRect(-150, -11, 130, 22);
    ctx.fillStyle = "#E8E8E8";
    for (let i = 0; i < 4; i++) ctx.fillRect(-140 + i * 12, -11, 5, 22);
    ctx.fillStyle = "#2563EB";
    ctx.fillRect(-96, -11, 6, 22);
    // rivets
    ctx.fillStyle = "#0A0A0C";
    ctx.fillRect(-24, -6, 6, 6);
    ctx.fillRect(-24, 2, 6, 6);
    // blade
    ctx.fillStyle = "#C7CDD6";
    ctx.fillRect(-20, -26, 96, 52);
    ctx.fillStyle = "#9AA2AE";
    ctx.fillRect(-20, 18, 96, 8);
    // serrated edge
    ctx.fillStyle = "#7C8490";
    for (let i = 0; i < 6; i++) ctx.fillRect(72, -26 + i * 9, 6, 4);
    // engraving hint
    ctx.fillStyle = "#3A3F47";
    ctx.fillRect(0, -8, 56, 4);
    ctx.fillRect(0, 2, 40, 4);
    ctx.restore();
}

function drawPatty(ctx, x, y, rot, scale = 1) {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rot);
    ctx.scale(scale, scale);
    const px = 8;
    const rows = [
        [-3, 3],
        [-5, 5],
        [-6, 6],
        [-6, 6],
        [-5, 5],
        [-3, 3],
    ];
    rows.forEach(([a, b], r) => {
        for (let c = a; c <= b; c++) {
            const edge = c === a || c === b || r === 0 || r === rows.length - 1;
            const h = hash(c, r);
            ctx.fillStyle = edge
                ? "#3d2310"
                : h === 0
                  ? "#2f1a08"
                  : h <= 2
                    ? "#8a5a24"
                    : "#6b3f16";
            ctx.fillRect(c * px, (r - 3) * px, px, px);
        }
    });
    ctx.restore();
}

export default function PattyFlipGame() {
    const canvasRef = useRef(null);
    const bgRef = useRef(null);
    const bestRef = useRef(Number(localStorage.getItem("ge-best") || 0));
    const g = useRef({
        state: "idle",
        night: new URLSearchParams(window.location.search).get("night") === "1",
        power: 0,
        dir: 1,
        lastPower: 0,
        patty: null,
        restPatty: null,
        particles: [],
        floats: [],
        shake: 0,
        resolveT: 0,
        score: 0,
        combo: 0,
    });

    const [score, setScore] = useState(0);
    const [best, setBest] = useState(bestRef.current);
    const [combo, setCombo] = useState(0);
    const [msg, setMsg] = useState({ id: 0, text: "HOLD SPACE TO CHARGE", kind: "idle" });
    const [sfxOn, setSfxOn] = useState(true);
    const [crt, setCrt] = useState(true);
    const [holding, setHolding] = useState(false);
    const [night, setNight] = useState(
        () => new URLSearchParams(window.location.search).get("night") === "1",
    );
    const [boardOpen, setBoardOpen] = useState(false);
    const [submitOpen, setSubmitOpen] = useState(false);
    const [pendingScore, setPendingScore] = useState(0);
    const [username, setUsername] = useState(localStorage.getItem("ge-username") || "");
    const [portrait, setPortrait] = useState(false);

    const modalsRef = useRef(false);
    modalsRef.current = submitOpen || boardOpen;

    const startCharge = useCallback(() => {
        const s = g.current;
        if (s.state !== "idle" || modalsRef.current) return;
        s.state = "charging";
        s.power = 0;
        s.dir = 1;
        setHolding(true);
        setMsg({ id: Date.now(), text: "RELEASE TO FLIP!", kind: "charge" });
        sfx.startCharge();
    }, []);

    const cancelCharge = useCallback(() => {
        const s = g.current;
        if (s.state !== "charging") return;
        s.state = "idle";
        s.power = 0;
        setHolding(false);
        sfx.stopCharge();
        setMsg({ id: Date.now(), text: "HOLD SPACE TO CHARGE", kind: "idle" });
    }, []);

    const release = useCallback(() => {
        const s = g.current;
        if (s.state !== "charging") return;
        const jit = 6 + (s.combo >= 20 ? Math.min(6, Math.floor((s.combo - 15) / 5)) : 0);
        const p = Math.max(0, Math.min(100, s.power + (Math.random() * jit * 2 - jit)));
        s.lastPower = p;
        s.throw = { t: 0, from: -0.04 - s.power * 0.002, launched: false };
        s.state = "throwing";
        setHolding(false);
        setMsg({ id: Date.now(), text: "", kind: "none" });
        sfx.stopCharge();
    }, []);

    const releaseRef = useRef(release);
    releaseRef.current = release;
    const cancelRef = useRef(cancelCharge);
    cancelRef.current = cancelCharge;
    const spaceHeldRef = useRef(false);
    const startChargeRef = useRef(startCharge);
    startChargeRef.current = startCharge;

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.code === "Space" && !(e.target instanceof HTMLInputElement)) {
                e.preventDefault();
                if (!e.repeat) {
                    spaceHeldRef.current = true;
                    startCharge();
                }
            }
        };
        const onKeyUp = (e) => {
            if (e.code === "Space") {
                e.preventDefault();
                spaceHeldRef.current = false;
                releaseRef.current();
            }
        };
        const onPointerUp = () => {
            spaceHeldRef.current = false;
            releaseRef.current();
        };
        const onBlur = () => {
            spaceHeldRef.current = false;
            cancelRef.current();
        };
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);
        window.addEventListener("pointerup", onPointerUp);
        window.addEventListener("blur", onBlur);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
            window.removeEventListener("pointerup", onPointerUp);
            window.removeEventListener("blur", onBlur);
        };
    }, [startCharge]);

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 640px) and (orientation: portrait)");
        const apply = () => setPortrait(mq.matches);
        apply();
        mq.addEventListener("change", apply);
        return () => mq.removeEventListener("change", apply);
    }, []);

    useEffect(() => {
        const img = new Image();
        img.src = "/assets/asset1.png";
        img.onload = () => {
            bgRef.current = img;
        };
        if (document.fonts?.load) {
            document.fonts.load('22px "Press Start 2P"');
        }
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx2d = canvas.getContext("2d");
        let raf;
        let last = performance.now();

        const spawnFlames = (x, y, n) => {
            const s = g.current;
            for (let i = 0; i < n; i++) {
                s.particles.push({
                    x: x + (Math.random() - 0.5) * 80,
                    y,
                    vx: (Math.random() - 0.5) * 160,
                    vy: -120 - Math.random() * 320,
                    life: 0.5 + Math.random() * 0.5,
                    max: 1,
                    color: FLAME_COLORS[(Math.random() * 4) | 0],
                    size: 6 + Math.random() * 10,
                    grav: -200,
                });
            }
        };

        const success = (x) => {
            const s = g.current;
            const perfect = s.lastPower >= SWEET[0] && s.lastPower <= SWEET[1];
            s.combo += 1;
            if (s.combo >= NIGHT_COMBO && !s.night) {
                s.night = true;
                setNight(true);
                s.floats.push({
                    x: W / 2 - 230,
                    y: 250,
                    text: "NIGHT SHIFT!",
                    color: "#38BDF8",
                    life: 1.6,
                    max: 1.6,
                });
            }
            const tNow = s.combo >= 20 ? Math.min(6, Math.floor((s.combo - 15) / 5)) : 0;
            if (tNow > (s.tier || 0) && s.combo < WIN_COMBO) {
                s.tier = tNow;
                s.floats.push({
                    x: W / 2 - 300,
                    y: 200,
                    text: "ZOOM OUT — HARDER!",
                    color: "#FF9500",
                    life: 1.6,
                    max: 1.6,
                });
            }
            if (s.combo === NEON_COMBO) {
                s.floats.push({
                    x: W / 2 - 250,
                    y: 360,
                    text: "NEON SHIFT!",
                    color: "#FF2D95",
                    life: 1.6,
                    max: 1.6,
                });
            }
            if (s.combo === MOVE_COMBO) {
                s.floats.push({
                    x: W / 2 - 320,
                    y: 310,
                    text: "THE GRILL FIGHTS BACK!",
                    color: "#FF3B30",
                    life: 1.6,
                    max: 1.6,
                });
            }
            let pts = 100 * s.combo + (perfect ? 50 : 0);
            if (s.combo >= WIN_COMBO) {
                pts += 1000;
                s.resetComboAfter = true;
                s.shake = 22;
                for (let i = 0; i < 90; i++) {
                    spawnFlames(x + (Math.random() - 0.5) * 520, GRILL.y - Math.random() * 260);
                }
                s.floats.push({
                    x: W / 2 - 200,
                    y: 190,
                    text: "YOU WIN!",
                    color: "#FFD60A",
                    life: 2.4,
                    max: 2.4,
                });
                s.floats.push({
                    x: W / 2 - 440,
                    y: 255,
                    text: "GRILL ENFORCER CHAMPION — +1000",
                    color: "#F2F2F7",
                    life: 2.4,
                    max: 2.4,
                });
            }
            s.score += pts;
            setScore(s.score);
            setCombo(s.combo);
            if (s.score > bestRef.current) {
                bestRef.current = s.score;
                setBest(s.score);
                localStorage.setItem("ge-best", String(s.score));
            }
            s.restPatty = { x, y: GRILL.y - 20 };
            s.patty = null;
            s.state = "resolved";
            s.resolveT = 1.15;
            s.shake = perfect ? 16 : 9;
            spawnFlames(x, GRILL.y, perfect ? 44 : 26);
            s.floats.push({
                x: Math.min(Math.max(x - 70, 30), W - 220),
                y: GRILL.y - 90,
                text: `+${pts}`,
                color: perfect ? "#FFD60A" : "#F2F2F7",
                life: 1.1,
                max: 1.1,
            });
            setMsg({
                id: Date.now(),
                text: perfect
                    ? "PERFECT FLIP!"
                    : GOOD_MSGS[(Math.random() * GOOD_MSGS.length) | 0],
                kind: perfect ? "perfect" : "good",
            });
            if (perfect) sfx.playPerfect();
            else sfx.playScore();
        };

        const miss = (x, offScreen) => {
            const s = g.current;
            s.combo = 0;
            setCombo(0);
            if (s.night) {
                s.night = false;
                setNight(false);
            }
            if (s.score > 0 && s.score > Number(localStorage.getItem("ge-submitted") || 0)) {
                setPendingScore(s.score);
                setSubmitOpen(true);
            }
            s.patty = null;
            s.state = "resolved";
            s.resolveT = 1.0;
            s.shake = 5;
            if (!offScreen) {
                s.restPatty = { x, y: GROUND - 14 };
                for (let i = 0; i < 18; i++) {
                    s.particles.push({
                        x: x + (Math.random() - 0.5) * 40,
                        y: GROUND - 8,
                        vx: (Math.random() - 0.5) * 220,
                        vy: -60 - Math.random() * 160,
                        life: 0.5 + Math.random() * 0.3,
                        max: 0.8,
                        color: ["#6b3f16", "#3d2310", "#8a5a24"][(Math.random() * 3) | 0],
                        size: 5 + Math.random() * 7,
                        grav: 700,
                    });
                }
            }
            setMsg({
                id: Date.now(),
                text: offScreen
                    ? "TOO MUCH MUSTARD!"
                    : MISS_MSGS[(Math.random() * MISS_MSGS.length) | 0],
                kind: "miss",
            });
            sfx.playMiss();
        };

        const update = (dt) => {
            const s = g.current;
            if (s.shake > 0) s.shake = Math.max(0, s.shake - 55 * dt);

            const tierNow = Math.max(TIER_PARAM, s.combo >= 20 ? Math.min(6, Math.floor((s.combo - 15) / 5)) : 0);
            const zoomTarget = 1 - tierNow * 0.08;
            s.zoom = (s.zoom || 1) + (zoomTarget - (s.zoom || 1)) * Math.min(dt * 2, 1);

            if (s.state === "charging") {
                s.power += s.dir * (CHARGE_SPEED + tierNow * 8) * dt;
                if (s.power >= 100) {
                    s.power = 100;
                    s.dir = -1;
                } else if (s.power <= 0) {
                    s.power = 0;
                    s.dir = 1;
                }
            }

            s.time = (s.time || 0) + dt;
            const moveTarget = s.combo >= MOVE_COMBO ? 1 : 0;
            s.moveAmt = (s.moveAmt || 0) + (moveTarget - (s.moveAmt || 0)) * Math.min(dt * 2, 1);
            s.grillOff = Math.sin(s.time * (1.6 + tierNow * 0.15)) * (55 + tierNow * 5) * s.moveAmt;

            // spatula angle: flick curve while throwing, wind-back while charging, rest otherwise
            if (s.throw) {
                const k = Math.min(s.throw.t / 0.22, 1);
                const e = 1 - Math.pow(1 - k, 3);
                s.dispAng = s.throw.from + (0.6 - s.throw.from) * e;
            } else {
                const target = s.state === "charging" ? -0.04 - s.power * 0.002 : 0;
                s.dispAng = (s.dispAng || 0) + (target - (s.dispAng || 0)) * Math.min(dt * 14, 1);
            }

            if (s.state === "throwing" && s.throw) {
                s.throw.t += dt;
                if (s.throw.t >= 0.13 && !s.throw.launched) {
                    s.throw.launched = true;
                    const p = s.lastPower;
                    const k = 0.13 / 0.22;
                    const e = 1 - Math.pow(1 - k, 3);
                    const ang = s.throw.from + (0.6 - s.throw.from) * e;
                    const tipX = LAUNCH.x + Math.cos(ang) * 95 + Math.sin(ang) * 42.5;
                    const tipY = LAUNCH.y + 17 + Math.sin(ang) * 95 - Math.cos(ang) * 42.5;
                    const v = 430 + p * 10;
                    s.patty = {
                        x: tipX,
                        y: tipY,
                        vx: Math.cos(ANGLE) * v,
                        vy: -Math.sin(ANGLE) * v,
                        rot: 0,
                        vr: 7 + p * 0.09,
                    };
                    s.state = "flying";
                    sfx.playLaunch();
                }
                if (s.throw.t > 0.4) s.throw = null;
            }

            if (s.state === "flying" && s.patty) {
                const p = s.patty;
                const prevY = p.y;
                p.vy += GRAV * dt;
                p.x += p.vx * dt;
                p.y += p.vy * dt;
                p.rot += p.vr * dt;
                if (Math.random() < 0.5) {
                    s.particles.push({
                        x: p.x,
                        y: p.y,
                        vx: -p.vx * 0.04,
                        vy: -30,
                        life: 0.3,
                        max: 0.3,
                        color: "rgba(255,255,255,0.45)",
                        size: 5,
                        grav: -100,
                    });
                }
                if (p.vy > 0 && prevY < GRILL.y && p.y >= GRILL.y) {
                    const gx1 = GRILL.x1 + (s.grillOff || 0);
                    const gx2 = GRILL.x2 + (s.grillOff || 0);
                    if (p.x > gx1 && p.x < gx2) {
                        success(p.x);
                    }
                }
                if (s.state === "flying") {
                    if (p.y > GROUND) miss(Math.min(p.x, W - 60), false);
                    else if (p.x > W + 120) miss(0, true);
                }
            }

            if (s.state === "resolved") {
                s.resolveT -= dt;
                if (s.resolveT <= 0) {
                    s.state = "idle";
                    s.restPatty = null;
                    if (s.resetComboAfter) {
                        s.resetComboAfter = false;
                        s.combo = 0;
                        setCombo(0);
                        s.tier = 0;
                        if (s.night) {
                            s.night = false;
                            setNight(false);
                        }
                        setMsg({ id: Date.now(), text: "NEW RUN — HOLD SPACE", kind: "idle" });
                    } else {
                        setMsg({ id: Date.now(), text: "HOLD SPACE TO CHARGE", kind: "idle" });
                    }
                    if (spaceHeldRef.current) startChargeRef.current();
                }
            }

            s.particles = s.particles.filter((pt) => {
                pt.life -= dt;
                pt.x += pt.vx * dt;
                pt.y += pt.vy * dt;
                pt.vy += (pt.grav || 0) * dt;
                return pt.life > 0;
            });
            s.floats = s.floats.filter((f) => {
                f.life -= dt;
                f.y -= 55 * dt;
                return f.life > 0;
            });

        };

        const draw = (t) => {
            const s = g.current;
            ctx2d.fillStyle = "#060608";
            ctx2d.fillRect(0, 0, W, H);
            ctx2d.save();
            if (s.shake > 0.3) {
                ctx2d.translate(
                    (Math.random() - 0.5) * s.shake,
                    (Math.random() - 0.5) * s.shake,
                );
            }
            const zoom = s.zoom || 1;
            if (zoom < 0.999) {
                ctx2d.translate(W / 2, H * 0.55);
                ctx2d.scale(zoom, zoom);
                ctx2d.translate(-W / 2, -H * 0.55);
            }
            if (bgRef.current) {
                ctx2d.drawImage(bgRef.current, 0, 0, W, H);
            } else {
                ctx2d.fillStyle = "#1E1E2E";
                ctx2d.fillRect(0, 0, W, H);
            }

            // cover the painted HUD in the artwork (world space, follows the zoom)
            ctx2d.fillStyle = "#0B0B0E";
            ctx2d.fillRect(0, 0, 512, 80);
            ctx2d.fillRect(995, 0, W - 995, 66);
            ctx2d.fillRect(1324, 66, W - 1324, 76);

            // night shift mode (5x combo): dark sky, stars, string lights
            if (s.night) {
                ctx2d.fillStyle = "rgba(7,9,38,0.55)";
                ctx2d.fillRect(0, 0, W, H);
                STARS.forEach((st, i) => {
                    ctx2d.globalAlpha = 0.3 + 0.5 * Math.abs(Math.sin(t * 0.002 + i));
                    ctx2d.fillStyle = "#F8FAFC";
                    ctx2d.fillRect(st[0], st[1], 3, 3);
                });
                ctx2d.globalAlpha = 1;
                const strands = [
                    { x0: 480, y0: 150, x1: 1180, y1: 185, sag: 85 },
                    { x0: 400, y0: 235, x1: 1300, y1: 270, sag: 105 },
                ];
                strands.forEach((ln, si) => {
                    ctx2d.strokeStyle = "rgba(15,15,24,0.9)";
                    ctx2d.lineWidth = 4;
                    ctx2d.beginPath();
                    for (let i = 0; i <= 24; i++) {
                        const tt = i / 24;
                        const px2 = ln.x0 + (ln.x1 - ln.x0) * tt;
                        const py = ln.y0 + (ln.y1 - ln.y0) * tt + ln.sag * 4 * tt * (1 - tt);
                        if (i === 0) ctx2d.moveTo(px2, py);
                        else ctx2d.lineTo(px2, py);
                    }
                    ctx2d.stroke();
                    for (let i = 1; i < 12; i++) {
                        const tt = i / 12;
                        const bx = ln.x0 + (ln.x1 - ln.x0) * tt;
                        const by = ln.y0 + (ln.y1 - ln.y0) * tt + ln.sag * 4 * tt * (1 - tt);
                        const NEON = ["#00F0FF", "#FF2D95", "#B026FF"];
                        const neon = s.combo >= NEON_COMBO || NEON_PARAM;
                        const bulb = neon
                            ? NEON[(i + si) % 3]
                            : (i + si) % 2 === 0
                              ? "#FFD60A"
                              : "#FF9500";
                        const rad = neon ? 54 : 36;
                        const glow = ctx2d.createRadialGradient(bx, by + 8, 2, bx, by + 8, rad);
                        glow.addColorStop(0, `${bulb}${neon ? "E6" : "D9"}`);
                        glow.addColorStop(1, `${bulb}00`);
                        ctx2d.fillStyle = glow;
                        ctx2d.fillRect(bx - rad, by + 8 - rad, rad * 2, rad * 2);
                        ctx2d.fillStyle = bulb;
                        ctx2d.fillRect(bx - 4, by + 2, neon ? 10 : 8, neon ? 14 : 12);
                    }
                });
            }

            // moving grill rack (10x combo and up)
            const gOff = s.grillOff || 0;
            if ((s.moveAmt || 0) > 0.05) {
                const gx1 = GRILL.x1 + gOff;
                const gx2 = GRILL.x2 + gOff;
                ctx2d.fillStyle = "#15151B";
                ctx2d.fillRect(gx1, GRILL.y - 10, gx2 - gx1, 14);
                ctx2d.fillStyle = "#33333F";
                for (let x = gx1 + 8; x < gx2 - 8; x += 20) {
                    ctx2d.fillRect(x, GRILL.y - 8, 10, 10);
                }
                ctx2d.globalAlpha = 0.55 * s.moveAmt;
                ctx2d.strokeStyle = "#FFD60A";
                ctx2d.lineWidth = 3;
                ctx2d.strokeRect(gx1 - 4, GRILL.y - 14, gx2 - gx1 + 8, 22);
                ctx2d.globalAlpha = 1;
            }

            // ambient grill flames — layered tongues with glow and rising embers
            const gcx = GRILL.x1 + gOff + (GRILL.x2 - GRILL.x1) / 2;
            const gglow = ctx2d.createRadialGradient(gcx, GRILL.y, 10, gcx, GRILL.y, 300);
            gglow.addColorStop(0, "rgba(255,120,20,0.30)");
            gglow.addColorStop(1, "rgba(255,120,20,0)");
            ctx2d.fillStyle = gglow;
            ctx2d.fillRect(gcx - 300, GRILL.y - 300, 600, 320);
            for (let i = 0; i < 12; i++) {
                const fx = GRILL.x1 + gOff + 26 + i * ((GRILL.x2 - GRILL.x1 - 52) / 11);
                const fbase = GRILL.y + 6;
                const fh =
                    14 +
                    24 * Math.abs(Math.sin(t * 0.004 + i * 1.7)) +
                    8 * Math.sin(t * 0.013 + i * 3.3);
                ctx2d.globalAlpha = 0.92;
                ctx2d.fillStyle = "#E82E14";
                ctx2d.fillRect(fx, fbase - fh * 0.45, 15, fh * 0.45);
                ctx2d.fillStyle = "#FF9500";
                ctx2d.fillRect(fx + 2.5, fbase - fh * 0.8, 10, fh * 0.38);
                ctx2d.fillStyle = "#FFD60A";
                ctx2d.fillRect(fx + 5, fbase - fh, 5, fh * 0.25);
                ctx2d.globalAlpha = 1;
                if (Math.random() < 0.05) {
                    s.particles.push({
                        x: fx + 6,
                        y: fbase - fh,
                        vx: (Math.random() - 0.5) * 40,
                        vy: -60 - Math.random() * 120,
                        life: 0.6 + Math.random() * 0.4,
                        max: 1,
                        color: Math.random() < 0.5 ? "#FFD60A" : "#FF9500",
                        size: 3,
                        grav: -60,
                    });
                }
            }

            // the little line — predicted arc while charging
            if (s.state === "charging") {
                const v = 430 + s.power * 10;
                const wang = -0.04 - s.power * 0.002;
                let sx = LAUNCH.x + Math.cos(wang) * 95 + Math.sin(wang) * 42.5;
                let sy = LAUNCH.y + 17 + Math.sin(wang) * 95 - Math.cos(wang) * 42.5;
                let svx = Math.cos(ANGLE) * v;
                let svy = -Math.sin(ANGLE) * v;
                ctx2d.fillStyle = "rgba(255,59,48,0.9)";
                for (let i = 0; i < 26; i++) {
                    const step = 0.055;
                    svy += GRAV * step;
                    sx += svx * step;
                    sy += svy * step;
                    if (sy > GROUND || sx > W) break;
                    if (i % 2 === 0) ctx2d.fillRect(sx, sy, 9, 9);
                }
            }

            // the enforcer spatula — always on duty
            const sang =
                (s.dispAng || 0) + (s.state === "idle" ? Math.sin(t * 0.003) * 0.012 : 0);
            drawSpatula(ctx2d, LAUNCH.x, LAUNCH.y + 17, sang);
            if (
                s.state === "idle" ||
                s.state === "charging" ||
                (s.state === "throwing" && s.throw && !s.throw.launched)
            ) {
                const tipX = LAUNCH.x + Math.cos(sang) * 95 + Math.sin(sang) * 42.5;
                const tipY = LAUNCH.y + 17 + Math.sin(sang) * 95 - Math.cos(sang) * 42.5;
                drawPatty(ctx2d, tipX, tipY, sang * 0.4);
                if (s.state === "charging") {
                    ctx2d.globalAlpha = 0.25 + 0.2 * Math.sin(t * 0.02);
                    ctx2d.fillStyle =
                        s.power >= SWEET[0] && s.power <= SWEET[1]
                            ? "#FFD60A"
                            : "#FF3B30";
                    ctx2d.fillRect(LAUNCH.x - 60, LAUNCH.y + 14, 120, 6);
                    ctx2d.globalAlpha = 1;
                }
            } else if (s.state === "flying" && s.patty) {
                drawPatty(ctx2d, s.patty.x, s.patty.y, s.patty.rot);
            } else if (s.restPatty) {
                drawPatty(ctx2d, s.restPatty.x, s.restPatty.y, 0);
            }

            s.particles.forEach((pt) => {
                ctx2d.globalAlpha = Math.max(pt.life / pt.max, 0);
                ctx2d.fillStyle = pt.color;
                ctx2d.fillRect(pt.x, pt.y, pt.size, pt.size);
            });
            ctx2d.globalAlpha = 1;

            ctx2d.font = '22px "Press Start 2P", monospace';
            s.floats.forEach((f) => {
                ctx2d.globalAlpha = Math.max(f.life / f.max, 0);
                ctx2d.fillStyle = f.color;
                ctx2d.fillText(f.text, f.x, f.y);
            });
            ctx2d.globalAlpha = 1;
            ctx2d.restore();

            // ember frame around the shrunken world
            if ((s.zoom || 1) < 0.999) {
                const z = s.zoom;
                ctx2d.strokeStyle = "rgba(255,59,48,0.55)";
                ctx2d.lineWidth = 4;
                ctx2d.strokeRect((W / 2) * (1 - z), H * 0.55 * (1 - z), W * z, H * z);
            }
        };

        const loop = (t) => {
            const dt = Math.min((t - last) / 1000, 0.05);
            last = t;
            update(dt);
            draw(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, []);

    const toggleSfx = () => {
        const next = !sfxOn;
        setSfxOn(next);
        sfx.setSfxEnabled(next);
    };

    const submitScore = async () => {
        const name = username.trim();
        if (name.length < 2) {
            toast.error("TAG TOO SHORT — 2+ CHARS");
            return;
        }
        try {
            const res = await fetch(`${API}/leaderboard`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ username: name, score: pendingScore }),
            });
            if (!res.ok) throw new Error("bad");
            localStorage.setItem("ge-username", name);
            localStorage.setItem("ge-submitted", String(pendingScore));
            setSubmitOpen(false);
            toast.success("SCORE POSTED TO THE BOARD");
        } catch (e) {
            toast.error("COULD NOT POST — TRY AGAIN");
        }
    };

    const skipSubmit = () => {
        localStorage.setItem("ge-submitted", String(pendingScore));
        setSubmitOpen(false);
    };

    const msgColor =
        msg.kind === "perfect"
            ? "text-[#FFD60A]"
            : msg.kind === "good"
              ? "text-[#F2F2F7]"
              : msg.kind === "miss"
                ? "text-[#FF3B30]"
                : msg.kind === "charge"
                  ? "text-[#38BDF8]"
                  : "text-[#F2F2F7]/80";

    return (
        <div
            data-testid="patty-flip-game"
            className={`relative select-none overflow-hidden border-2 border-[#2A2A36] bg-[#0B0B0E] shadow-[0_0_80px_rgba(255,59,48,0.08)] ${
                portrait ? "w-full" : "mx-auto w-fit"
            }`}
        >
            <canvas
                ref={canvasRef}
                width={W}
                height={H}
                data-testid="patty-flip-game-canvas"
                className={`pixelated block h-auto ${portrait ? "" : "mx-auto w-auto max-w-full"}`}
                style={
                    portrait
                        ? { width: "136.5%", maxWidth: "none", marginLeft: "-31.3%" }
                        : { maxHeight: "calc(100svh - 235px)" }
                }
            />
            {crt && (
                <>
                    <div className="crt-overlay pointer-events-none absolute inset-0 z-20" />
                    <div className="crt-vignette pointer-events-none absolute inset-0 z-20" />
                </>
            )}
            <div className="noise-layer z-20" />

            {/* HUD */}
            <div className="pointer-events-none absolute inset-0 z-30">
                <div className="absolute left-2 top-2 flex items-center gap-2 border border-[#2A2A36] bg-[#0B0B0E]/95 px-2.5 py-2 sm:left-3 sm:top-3 sm:px-4">
                    <Flame className="h-3.5 w-3.5 text-[#FF3B30]" strokeWidth={2.5} />
                    <span className="font-pixel hidden text-[8px] tracking-wider text-[#F2F2F7] min-[420px]:inline sm:text-[10px]">
                        GRILL ENFORCERS
                    </span>
                </div>

                <div className="absolute right-2 top-2 flex items-center gap-1.5 sm:right-3 sm:top-3 sm:gap-2">
                    <div className="flex items-center gap-2.5 border border-[#2A2A36] bg-[#0B0B0E]/95 px-3 py-2 sm:gap-4 sm:px-4">
                        <span className="font-pixel text-[8px] sm:text-[10px]">
                            <span className="text-[#A1A1B2]">SCORE </span>
                            <span data-testid="game-score-display" className="text-[#FFD60A]">
                                {score}
                            </span>
                        </span>
                        <span className="hidden font-pixel text-[10px] sm:inline">
                            <span className="text-[#A1A1B2]">BEST </span>
                            <span data-testid="game-best-display" className="text-[#38BDF8]">
                                {best}
                            </span>
                        </span>
                        <span className="font-pixel text-[8px] sm:text-[10px]">
                            <span className="text-[#A1A1B2]">COMBO </span>
                            <span
                                data-testid="game-combo-display"
                                className={night ? "text-[#38BDF8]" : "text-[#FF3B30]"}
                            >
                                x{combo}
                            </span>
                        </span>
                    </div>
                    <button
                        data-testid="leaderboard-button"
                        onClick={() => setBoardOpen(true)}
                        className="pointer-events-auto border border-[#2A2A36] bg-[#0B0B0E]/95 p-2 text-[#A1A1B2] transition-colors hover:border-[#FFD60A] hover:text-[#FFD60A]"
                        aria-label="Open leaderboard"
                    >
                        <Trophy className="h-4 w-4" />
                    </button>
                    <button
                        data-testid="game-sfx-toggle-button"
                        onClick={toggleSfx}
                        className="pointer-events-auto border border-[#2A2A36] bg-[#0B0B0E]/95 p-2 text-[#A1A1B2] transition-colors hover:border-[#FF3B30] hover:text-[#FF3B30]"
                        aria-label="Toggle sound effects"
                    >
                        {sfxOn ? (
                            <Volume2 className="h-4 w-4" />
                        ) : (
                            <VolumeX className="h-4 w-4" />
                        )}
                    </button>
                    <button
                        data-testid="game-crt-toggle-button"
                        onClick={() => setCrt((c) => !c)}
                        className={`pointer-events-auto border bg-[#0B0B0E]/95 p-2 transition-colors ${
                            crt
                                ? "border-[#38BDF8] text-[#38BDF8]"
                                : "border-[#2A2A36] text-[#A1A1B2] hover:border-[#38BDF8] hover:text-[#38BDF8]"
                        }`}
                        aria-label="Toggle CRT scanlines"
                    >
                        <ScanLine className="h-4 w-4" />
                    </button>
                </div>

                {/* center message */}
                <div className="absolute inset-x-0 top-[32%] flex justify-center sm:top-[20%]">
                    <AnimatePresence mode="wait">
                        {msg.text && (
                            <motion.div
                                key={msg.id}
                                initial={{ opacity: 0, scale: 0.7, y: 12 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 1.1, y: -10 }}
                                transition={{ type: "spring", stiffness: 320, damping: 22 }}
                                data-testid="game-message"
                                className={`font-pixel text-center text-[11px] drop-shadow-[0_4px_0_rgba(0,0,0,0.9)] sm:text-xl md:text-2xl ${msgColor} ${
                                    msg.kind === "idle" ? "blink" : ""
                                }`}
                            >
                                {msg.text}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* bottom controls */}
                <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-2 p-3 sm:gap-3 sm:p-5">
                    <button
                        data-testid="game-charge-launch-button"
                        onPointerDown={(e) => {
                            e.preventDefault();
                            spaceHeldRef.current = true;
                            startCharge();
                        }}
                        onContextMenu={(e) => e.preventDefault()}
                        className={`pointer-events-auto w-full max-w-xl touch-none border-2 py-3.5 font-pixel text-[9px] tracking-wider transition-[transform,background-color,color,border-color] duration-100 sm:py-4 sm:text-[11px] ${
                            holding
                                ? "scale-[0.98] border-[#FFD60A] bg-[#FFD60A] text-[#0A0A0C]"
                                : "border-[#FF3B30] bg-[#0B0B0E]/90 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-[#0A0A0C]"
                        }`}
                    >
                        {holding ? "RELEASE TO FLIP!" : "HOLD TO CHARGE — RELEASE TO FLIP"}
                    </button>
                    <span className="hidden font-pixel text-[7px] text-[#A1A1B2]/70 sm:block sm:text-[8px]">
                        OR HOLD [SPACE]
                    </span>
                </div>
            </div>

            {/* high score tag submit */}
            <AnimatePresence>
                {submitOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        data-testid="score-submit-modal"
                        className="absolute inset-0 z-40 flex items-center justify-center bg-[#060608]/85 p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 16 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95 }}
                            transition={{ type: "spring", stiffness: 300, damping: 24 }}
                            className="w-full max-w-sm border-2 border-[#FFD60A] bg-[#0B0B0E] p-5 text-center sm:p-6"
                        >
                            <p className="font-pixel text-[9px] leading-relaxed text-[#FFD60A] sm:text-[10px]">
                                RUN OVER — SCORE {pendingScore}
                            </p>
                            <p className="font-pixel mt-3 text-[7px] leading-relaxed text-[#A1A1B2] sm:text-[8px]">
                                ENTER YOUR TAG FOR THE LEADERBOARD
                            </p>
                            <input
                                data-testid="leaderboard-username-input"
                                value={username}
                                onChange={(e) => setUsername(e.target.value.slice(0, 14))}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") submitScore();
                                }}
                                placeholder="YOUR TAG"
                                maxLength={14}
                                className="font-pixel mt-4 w-full border-2 border-[#2A2A36] bg-[#121216] px-3 py-3 text-center text-[10px] uppercase tracking-wider text-[#F2F2F7] outline-none placeholder:text-[#A1A1B2]/40 focus:border-[#FFD60A]"
                            />
                            <div className="mt-4 flex gap-2">
                                <button
                                    data-testid="submit-score-button"
                                    onClick={submitScore}
                                    className="flex-1 border-2 border-[#FFD60A] bg-[#FFD60A] px-4 py-3 font-pixel text-[8px] tracking-wider text-[#0A0A0C] transition-colors hover:bg-transparent hover:text-[#FFD60A] sm:text-[9px]"
                                >
                                    POST SCORE
                                </button>
                                <button
                                    data-testid="skip-score-button"
                                    onClick={skipSubmit}
                                    className="border-2 border-[#2A2A36] px-4 py-3 font-pixel text-[8px] tracking-wider text-[#A1A1B2] transition-colors hover:border-[#F2F2F7] hover:text-[#F2F2F7] sm:text-[9px]"
                                >
                                    SKIP
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <LeaderboardModal
                open={boardOpen}
                onClose={() => setBoardOpen(false)}
                currentScore={score}
            />
        </div>
    );
}
